import React from "react";
import { Link, useParams } from "react-router-dom";

import {
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
  FaEye,
} from "react-icons/fa";

import {
  useGetEnglishQuizAttemptsQuery,
} from "../../../slices/englishQuizAttemptApiSlice";


const EnglishQuizAttemptsAdminScreen = () => {
  const { quizId } = useParams();

  const {
    data: attempts = [],
    isLoading,
    error,
  } = useGetEnglishQuizAttemptsQuery(quizId);


  // ======================================================
  // Loading
  // ======================================================

  if (isLoading) {
    return (
      <div className="p-10 text-center">
        Loading student attempts...
      </div>
    );
  }


  // ======================================================
  // Error
  // ======================================================

  if (error) {
    return (
      <div className="p-10 text-center text-red-600">
        Unable to load student attempts.
      </div>
    );
  }


  return (
    <div className="min-h-screen bg-gray-50 p-6">

      <div className="max-w-7xl mx-auto">

        {/* ==================================================
            Header
        ================================================== */}

        <div className="mb-6">

          <Link
            to="/admin/english-quizzes"
            className="
              text-blue-600
              inline-block
              mb-4
            "
          >
            ← Back to English Quizzes
          </Link>

          <h1 className="text-3xl font-bold text-gray-800">
            Student Attempts
          </h1>

          <p className="mt-2 text-gray-600">
            View all student attempts for this English quiz.
          </p>

        </div>


        {/* ==================================================
            Summary
        ================================================== */}

        <div
          className="
            mb-6
            rounded-2xl
            bg-white
            p-6
            shadow
          "
        >

          <p className="text-sm text-gray-500">
            Total Attempts
          </p>

          <p className="mt-1 text-3xl font-bold text-gray-800">
            {attempts.length}
          </p>

        </div>


        {/* ==================================================
            No Attempts
        ================================================== */}

        {attempts.length === 0 && (
          <div
            className="
              rounded-2xl
              bg-white
              p-10
              shadow
              text-center
            "
          >

            <FaClock
              className="
                mx-auto
                text-4xl
                text-gray-400
              "
            />

            <h2 className="mt-4 text-xl font-semibold">
              No student attempts
            </h2>

            <p className="mt-2 text-gray-500">
              No student has attempted this English quiz yet.
            </p>

          </div>
        )}


        {/* ==================================================
            All Student Attempts
        ================================================== */}

        {attempts.length > 0 && (
          <div
            className="
              overflow-hidden
              rounded-2xl
              bg-white
              shadow
            "
          >

            <div className="overflow-x-auto">

              <table className="w-full text-left">

                <thead className="bg-gray-100">

                  <tr>

                    <th className="px-5 py-4">
                      #
                    </th>

                    <th className="px-5 py-4">
                      Student
                    </th>

                    <th className="px-5 py-4">
                      Attempt
                    </th>

                    <th className="px-5 py-4">
                      Questions
                    </th>

                    <th className="px-5 py-4">
                      Score
                    </th>

                    <th className="px-5 py-4">
                      Percentage
                    </th>

                    <th className="px-5 py-4">
                      Result
                    </th>

                    <th className="px-5 py-4">
                      Status
                    </th>

                    <th className="px-5 py-4">
                      Action
                    </th>

                  </tr>

                </thead>


                <tbody>

                  {attempts.map(
                    (attempt, index) => {

                      const student =
                        attempt.student;

                      const completed =
                        attempt.status ===
                        "completed";

                      const passed =
                        attempt.result ===
                        "pass";


                      return (
                        <tr
                          key={attempt._id}
                          className="
                            border-t
                            border-gray-100
                            hover:bg-gray-50
                          "
                        >

                          {/* Number */}

                          <td className="px-5 py-4">
                            {index + 1}
                          </td>


                          {/* Student */}

                          <td className="px-5 py-4">

                            <div className="font-semibold text-gray-800">
                              {student?.name ||
                                "Unknown Student"}
                            </div>

                            {student?.studentId && (
                              <div className="text-sm text-gray-500">
                                ID: {student.studentId}
                              </div>
                            )}

                            {student?.phone && (
                              <div className="text-sm text-gray-500">
                                {student.phone}
                              </div>
                            )}

                          </td>


                          {/* Attempt Number */}

                          <td className="px-5 py-4">
                            #{attempt.attemptNumber || 1}
                          </td>


                          {/* Questions */}

                          <td className="px-5 py-4">
                            {attempt.totalQuestions ||
                              attempt.questions?.length ||
                              0}
                          </td>


                          {/* Score */}

                          <td className="px-5 py-4">

                            <span className="font-semibold">
                              {attempt.obtainedMarks ?? 0}
                            </span>

                            <span className="text-gray-500">
                              {" / "}
                              {attempt.totalMarks ?? 0}
                            </span>

                          </td>


                          {/* Percentage */}

                          <td className="px-5 py-4">

                            <span className="font-semibold">
                              {attempt.percentage ?? 0}%
                            </span>

                          </td>


                          {/* Result */}

                          <td className="px-5 py-4">

                            {!completed ? (
                              <span className="text-gray-400">
                                —
                              </span>
                            ) : passed ? (
                              <span
                                className="
                                  inline-flex
                                  items-center
                                  gap-1
                                  rounded-full
                                  bg-green-100
                                  px-3
                                  py-1
                                  text-sm
                                  font-semibold
                                  text-green-700
                                "
                              >
                                <FaCheckCircle />
                                Pass
                              </span>
                            ) : (
                              <span
                                className="
                                  inline-flex
                                  items-center
                                  gap-1
                                  rounded-full
                                  bg-red-100
                                  px-3
                                  py-1
                                  text-sm
                                  font-semibold
                                  text-red-700
                                "
                              >
                                <FaTimesCircle />
                                Fail
                              </span>
                            )}

                          </td>


                          {/* Status */}

                          <td className="px-5 py-4">

                            {completed ? (
                              <span className="text-green-600">
                                Completed
                              </span>
                            ) : (
                              <span className="text-yellow-600">
                                {attempt.status ||
                                  "In Progress"}
                              </span>
                            )}

                          </td>


                          {/* View */}

                          <td className="px-5 py-4">

                            <Link
                              to={`/admin/english-quiz-attempts/${attempt._id}`}
                              className="
                                inline-flex
                                items-center
                                gap-2
                                rounded-lg
                                bg-blue-600
                                px-4
                                py-2
                                text-sm
                                font-semibold
                                text-white
                                hover:bg-blue-700
                              "
                            >
                              <FaEye />
                              View
                            </Link>

                          </td>

                        </tr>
                      );
                    },
                  )}

                </tbody>

              </table>

            </div>

          </div>
        )}

      </div>

    </div>
  );
};


export default EnglishQuizAttemptsAdminScreen;